// src/components/VoorschotAnalysis.js - Updated with persistent price settings
import React, { useState, useEffect } from 'react';
import '../css/VoorschotAnalysis.css';
import api from './api';
import { 
  calculatePredictedTotal, 
  isCurrentEnergyYear, 
  getLatestMonthWithData 
} from '../utils/energyCalculations';

const VoorschotAnalysis = ({ selectedEnergyYear }) => {
  const [analysisData, setAnalysisData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [energyPrices, setEnergyPrices] = useState({
    variablePricePerUnit: 0.600054, // Will be loaded from database
    fixedPricePerUnit: 0.717296     // Will be loaded from database
  });
  const [showPriceSettings, setShowPriceSettings] = useState(false);
  const [apartmentUsage, setApartmentUsage] = useState({});
  const [usageDataLoaded, setUsageDataLoaded] = useState(false);
  const [priceSettingsLoading, setPriceSettingsLoading] = useState(false);
  const [lastSavedPrices, setLastSavedPrices] = useState(null);
  const [residentsData, setResidentsData] = useState({});
  const [lastImportDate, setLastImportDate] = useState(null);

  // Apartment size configuration
  const APARTMENT_SIZES = {
    SMALL: 250,  // Small apartments get 250 fixed units
    LARGE: 375   // Large apartments get 375 fixed units
  };

  // Seasonality map for prediction calculations
  // const seasonalityMap = {
  //   '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
  //   '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
  //   '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
  // };

  const fetchLastImportDate = async () => {
  try {
    const response = await api.get('/api/ista/last-date');
    if (response.data && response.data.lastDate) {
      setLastImportDate(response.data.lastDate);
    }
  } catch (error) {
    console.error('Error fetching last import date:', error);
  }
};

  // Load price settings from database
  const loadResidentsData = async () => {
    try {
      console.log('📋 Loading residents data...');
      
      const response = await api.get('/api/bewoners/get-all');
      
      if (response.data && Array.isArray(response.data)) {
        const residentsMap = {};
        
        response.data.forEach(resident => {
          const apartmentNum = resident.nummer;
          if (apartmentNum && parseInt(apartmentNum) >= 211 && parseInt(apartmentNum) <= 305) {
            // Group residents by apartment number
            if (!residentsMap[apartmentNum]) {
              residentsMap[apartmentNum] = [];
            }
            
            // Only include current residents (not former residents)
            if (!resident.isFormerResident && resident.inhabited) {
              residentsMap[apartmentNum].push({
                naam: resident.naam,
                rol: resident.rol,
                email: resident.email
              });
            }
          }
        });
        
        setResidentsData(residentsMap);
        console.log(`✅ Loaded residents data for ${Object.keys(residentsMap).length} apartments`);
      }
    } catch (error) {
      console.error('❌ Error loading residents data:', error);
      // Continue without resident data if loading fails
    }
  }; //when component mounts or energy year changes
  useEffect(() => {
    if (selectedEnergyYear) {
    setLoading(true);
    fetchLastImportDate(); // Add this line
    loadPriceSettings();
    loadResidentsData();
   fetchUsageData();
    }
  }, [selectedEnergyYear]);

  useEffect(() => {
    if (selectedEnergyYear) {
      fetchUsageData();
    }
  }, [selectedEnergyYear]);

  useEffect(() => {
    if (usageDataLoaded) {
      calculateAnalysis();
    }
  }, [energyPrices, apartmentUsage, usageDataLoaded]);

  // Load price settings from database
  const loadPriceSettings = async () => {
    try {
      console.log(`📋 Loading price settings for ${selectedEnergyYear}`);
      
      const response = await api.get(`/api/price-settings/${selectedEnergyYear}`);
      
      if (response.data.success) {
        const { variablePricePerUnit, fixedPricePerUnit, lastUpdated, updatedBy } = response.data.data;
        
        setEnergyPrices({
          variablePricePerUnit,
          fixedPricePerUnit
        });
        
        setLastSavedPrices({
          variablePricePerUnit,
          fixedPricePerUnit,
          lastUpdated,
          updatedBy
        });
        
        console.log(`✅ Loaded price settings: Variable €${variablePricePerUnit}, Fixed €${fixedPricePerUnit}`);
      }
    } catch (error) {
      console.error('❌ Error loading price settings:', error);
      // Keep default values if loading fails
      console.log('Using default price settings');
    }
  };

  // Save price settings to database
  const savePriceSettings = async () => {
    setPriceSettingsLoading(true);
    try {
      console.log(`💾 Saving price settings for ${selectedEnergyYear}:`, energyPrices);
      
      const response = await api.put(`/api/price-settings/${selectedEnergyYear}`, {
        variablePricePerUnit: energyPrices.variablePricePerUnit,
        fixedPricePerUnit: energyPrices.fixedPricePerUnit
      });
      
      if (response.data.success) {
        const { lastUpdated, updatedBy } = response.data.data;
        
        setLastSavedPrices({
          ...energyPrices,
          lastUpdated,
          updatedBy
        });
        
        console.log(`✅ Price settings saved successfully`);
        alert(`Prijsinstellingen opgeslagen voor ${selectedEnergyYear}!`);
      }
    } catch (error) {
      console.error('❌ Error saving price settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Fout bij opslaan prijsinstellingen: ${errorMessage}`);
    } finally {
      setPriceSettingsLoading(false);
    }
  };

  // Reset price settings to default
  const resetPriceSettings = async () => {
    if (!window.confirm(`Prijsinstellingen resetten naar standaard waarden voor ${selectedEnergyYear}?`)) {
      return;
    }
    
    setPriceSettingsLoading(true);
    try {
      console.log(`🔄 Resetting price settings for ${selectedEnergyYear}`);
      
      const response = await api.delete(`/api/price-settings/${selectedEnergyYear}`);
      
      if (response.data.success) {
        const { variablePricePerUnit, fixedPricePerUnit, lastUpdated, updatedBy } = response.data.data;
        
        setEnergyPrices({
          variablePricePerUnit,
          fixedPricePerUnit
        });
        
        setLastSavedPrices({
          variablePricePerUnit,
          fixedPricePerUnit,
          lastUpdated,
          updatedBy
        });
        
        console.log(`✅ Price settings reset to defaults`);
        alert(`Prijsinstellingen gereset naar standaard waarden!`);
      }
    } catch (error) {
      console.error('❌ Error resetting price settings:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Fout bij resetten prijsinstellingen: ${errorMessage}`);
    } finally {
      setPriceSettingsLoading(false);
    }
  };

  // Calculate totals for all columns
  const calculateTotals = (data) => {
    if (!data || data.length === 0) {
      return {
        totalApartments: 0,
        totalPreviousYear: 0,
        totalActualUnits: 0,
        totalPredictedUnits: 0,
        totalVariableCosts: 0,
        totalFixedCosts: 0,
        totalPredictedCosts: 0,
        totalVoorschotten: 0,
        totalDifference: 0,
        averagePercentageDifference: 0
      };
    }

    const totals = data.reduce((acc, apartment) => {
      acc.totalPreviousYear += apartment.previousYearTotal || 0;
      acc.totalActualUnits += apartment.actualUnits || 0;
      acc.totalPredictedUnits += apartment.predictedUnits || 0;
      acc.totalVariableCosts += apartment.variableCosts || 0;
      acc.totalFixedCosts += apartment.fixedCosts || 0;
      acc.totalPredictedCosts += apartment.totalPredictedCosts || 0;
      acc.totalVoorschotten += apartment.totalVoorschotten || 0;
      acc.totalDifference += apartment.difference || 0;
      return acc;
    }, {
      totalPreviousYear: 0,
      totalActualUnits: 0,
      totalPredictedUnits: 0,
      totalVariableCosts: 0,
      totalFixedCosts: 0,
      totalPredictedCosts: 0,
      totalVoorschotten: 0,
      totalDifference: 0
    });

    // Calculate average percentage difference
    const validPercentages = data.filter(apt => apt.totalPredictedCosts > 0);
    const averagePercentageDifference = validPercentages.length > 0
      ? validPercentages.reduce((sum, apt) => sum + apt.percentageDifference, 0) / validPercentages.length
      : 0;

    return {
      totalApartments: data.length,
      averagePercentageDifference,
      ...totals
    };
  };

  // Fixed fetchUsageData function with correct previous year logic
const fetchUsageData = async () => {
  setLoading(true);
  try {
    console.log(`🔍 Fetching usage data for energy year ${selectedEnergyYear}`);
    
    // Fetch last import date
    await fetchLastImportDate();
    
    const usageResponse = await api.get('/api/ista/compare-energy-years');
    
    if (usageResponse.data && Array.isArray(usageResponse.data)) {
      const usageMap = {};
      
      // Filter only Toren apartments (211-305)
      const torenApartments = usageResponse.data.filter(apt => {
        const aptNum = parseInt(apt.homeNumber);
        return aptNum >= 211 && aptNum <= 305;
      });
      
      console.log(`Found ${torenApartments.length} Toren apartments with usage data`);
      
      // Calculate the exact previous energy year
      const [currentStartYear, currentEndYear] = selectedEnergyYear.split('-').map(Number);
      const previousEnergyYear = `${currentStartYear - 1}-${currentEndYear - 1}`;
      
      console.log(`Current energy year: ${selectedEnergyYear}, Previous energy year: ${previousEnergyYear}`);
      
      torenApartments.forEach(apartment => {
        const yearKeys = Object.keys(apartment)
          .filter(k => k.match(/^total\d{4}_\d{4}$/))
          .sort();
        
        if (yearKeys.length > 0) {
          // Get the current energy year data
          const targetYear = selectedEnergyYear.replace('-', '_');
          const currentYearKey = yearKeys.find(k => k.includes(targetYear));
          
          // Get the EXACT previous energy year data
          const previousYearTarget = previousEnergyYear.replace('-', '_');
          const previousYearKey = yearKeys.find(k => k.includes(previousYearTarget));
          
          // Get monthly data for current year
          const currentYearPattern = selectedEnergyYear;
          const currentYearMonthlyData = apartment[currentYearPattern] || {};
          
          // Calculate actual usage so far
          const actualSoFar = Object.values(currentYearMonthlyData).reduce((sum, val) => sum + (val || 0), 0);
          
          // Get the latest month with data
          const latestMonth = getLatestMonthWithData(currentYearMonthlyData);
          
          // Use the unified prediction function
          const predictedTotal = calculatePredictedTotal(
            actualSoFar,
            latestMonth,
            lastImportDate,
            isCurrentEnergyYear(selectedEnergyYear)
          );
          
          // Log the prediction for apartment 237 for debugging
          if (apartment.homeNumber === '237') {
            console.log(`🔍 Apartment 237 prediction:`, {
              actualSoFar,
              latestMonth,
              lastImportDate: lastImportDate ? new Date(lastImportDate).toLocaleDateString() : null,
              isCurrentYear: isCurrentEnergyYear(selectedEnergyYear),
              predictedTotal
            });
          }
          
          // Get the actual values
          const currentYearTotal = currentYearKey ? apartment[currentYearKey] : actualSoFar;
          const previousYearTotal = previousYearKey ? apartment[previousYearKey] : null;
          
          usageMap[apartment.homeNumber] = {
            currentYearActual: currentYearTotal,
            previousYearTotal: previousYearTotal,
            predictedTotal: predictedTotal,
            actualUnits: actualSoFar, // Changed from currentYearTotal to actualSoFar
            apartmentNumber: apartment.homeNumber,
            monthlyData: currentYearMonthlyData // Store monthly data for potential future use
          };
        }
      });
      
      setApartmentUsage(usageMap);
      setUsageDataLoaded(true);
      console.log(`✅ Loaded usage data for ${Object.keys(usageMap).length} apartments`);
    }
  } catch (error) {
    console.error('❌ Error fetching usage data:', error);
    alert('Could not load usage data. Please try again.');
  } finally {
    setLoading(false);
  }
};


  // Calculate the analysis comparing voorschotten vs predicted costs
  const calculateAnalysis = async () => {
    try {
      console.log('🧮 Calculating voorschot analysis...');
      
      // Get voorschotten data for the selected energy year
      const voorschotResponse = await api.get(`/api/voorschotten/energy-year/${selectedEnergyYear}`);
      
      if (!voorschotResponse.data.apartments) {
        console.log('No voorschot data available');
        return;
      }
      
      // Determine if this is the current energy year or a previous one
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1; // 1-12
      const currentCalendarYear = currentDate.getFullYear();
      const currentEnergyYearStart = currentMonth >= 7 ? currentCalendarYear : currentCalendarYear - 1;
      const currentEnergyYear = `${currentEnergyYearStart}-${currentEnergyYearStart + 1}`;
      
      const isCurrentEnergyYear = selectedEnergyYear === currentEnergyYear;
      
      console.log(`📅 Selected: ${selectedEnergyYear}, Current: ${currentEnergyYear}, Is current: ${isCurrentEnergyYear}`);
      
      const analysisResults = [];
      
      // Process each apartment
      voorschotResponse.data.apartments.forEach(apartment => {
        const apartmentNum = apartment.apartmentNumber;
        
        // Skip if not a Toren apartment
        const aptNumInt = parseInt(apartmentNum);
        if (aptNumInt < 211 || aptNumInt > 305) {
          return;
        }
        
        // Get usage data
        const usageData = apartmentUsage[apartmentNum];
        if (!usageData) {
          console.log(`⚠️ No usage data for apartment ${apartmentNum}`);
          return;
        }
        
        // Get resident name(s) - handle multiple residents
        let residentNames = 'Onbekend';
        const residentsForApartment = residentsData[apartmentNum];
        
        if (residentsForApartment && residentsForApartment.length > 0) {
          // Create a formatted string of resident names
          residentNames = residentsForApartment
            .map(resident => {
              let name = resident.naam || '';
              // Add role indicator if available
              if (resident.rol && resident.rol !== 'Eigenaar') {
                name += ` (${resident.rol})`;
              }
              return name;
            })
            .filter(name => name.trim() !== '')
            .join(', ');
          
          // Fallback if no valid names found
          if (!residentNames.trim()) {
            residentNames = 'Onbekend';
          }
        }
        
        // Calculate total voorschotten paid for the energy year
        const totalVoorschotten = apartment.energyYearVoorschotten.reduce(
          (sum, month) => sum + month.amount, 0
        );
        
        // 🔧 KEY FIX: Use actual units for previous years, predicted for current year
        const unitsForCalculation = isCurrentEnergyYear ? usageData.predictedTotal : usageData.actualUnits;
        
        console.log(`Apartment ${apartmentNum}: Using ${isCurrentEnergyYear ? 'predicted' : 'actual'} units: ${unitsForCalculation}`);
        
        // Calculate costs based on the appropriate units
        const variableCosts = unitsForCalculation * energyPrices.variablePricePerUnit;
        const fixedUnits = APARTMENT_SIZES[apartment.apartmentSize] || APARTMENT_SIZES.LARGE;
        const fixedCosts = fixedUnits * energyPrices.fixedPricePerUnit;
        const totalPredictedCosts = variableCosts + fixedCosts;
        
        // Calculate difference
        const difference = totalVoorschotten - totalPredictedCosts;
        const percentageDifference = totalPredictedCosts > 0 
          ? (difference / totalPredictedCosts) * 100 
          : 0;
        
        // Determine status: Must be BOTH over 10% AND over €180 difference
        let status = 'OK';
        let statusColor = 'green';
        const absDifference = Math.abs(difference);
        const absPercentage = Math.abs(percentageDifference);
        
        if (absDifference > 180 && absPercentage > 10) {
          if (difference < 0) {
            status = 'Te weinig betaald';
            statusColor = 'red';
          } else {
            status = 'Te veel betaald';
            statusColor = 'orange';
          }
        }
        
        analysisResults.push({
          apartmentNumber: apartmentNum,
          residentName: residentNames,
          apartmentSize: apartment.apartmentSize,
          previousYearTotal: usageData.previousYearTotal,
          actualUnits: usageData.actualUnits, 
          predictedUnits: usageData.predictedTotal,
          unitsUsedForCalculation: unitsForCalculation, // Add this for clarity
          isCurrentEnergyYear: isCurrentEnergyYear, // Add this for reference
          variableCosts,
          fixedCosts,
          totalPredictedCosts,
          totalVoorschotten,
          difference,
          percentageDifference,
          status,
          statusColor,
          // Additional details for the expanded view
          fixedUnits,
          monthlyAverage: totalVoorschotten / 12
        });
      });
      
      // Sort by apartment number
      analysisResults.sort((a, b) => 
        parseInt(a.apartmentNumber) - parseInt(b.apartmentNumber)
      );
      
      setAnalysisData(analysisResults);
      console.log(`✅ Analysis calculated for ${analysisResults.length} apartments`);
      
    } catch (error) {
      console.error('❌ Error calculating analysis:', error);
      alert('Could not calculate analysis. Please try again.');
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  // Sort the analysis data
  const sortedAnalysisData = React.useMemo(() => {
    if (!sortConfig.key) return analysisData;

    return [...analysisData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = 0;
      if (bVal === null || bVal === undefined) bVal = 0;

      // Special handling for percentage values
      if (sortConfig.key === 'percentageDifference') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Special handling for apartment numbers (numeric sort)
      if (sortConfig.key === 'apartmentNumber') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }

      // String comparison for text fields
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === 'ascending' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      return sortConfig.direction === 'ascending' ? aVal - bVal : bVal - aVal;
    });
  }, [analysisData, sortConfig]);

  // Handle price settings update (save to database)
  const handlePriceUpdate = async () => {
    await savePriceSettings();
    calculateAnalysis();
    setShowPriceSettings(false);
  };

  // Get sort indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return ' ↕️';
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format number
  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(Math.round(num));
  };

  // Calculate totals for display
  const totals = calculateTotals(sortedAnalysisData);

  if (loading) {
    return (
      <div className="voorschot-analysis">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Gegevens laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="voorschot-analysis">
      <div className="analysis-header">
        <h3>🎯 Voorschot Analyse - Toren Appartementen</h3>
        <p style={{ color: 'white' }}>
          Vergelijking van betaalde voorschotten vs werkelijke kosten voor {selectedEnergyYear}
          {analysisData.length > 0 && (
            <span style={{ display: 'block', fontSize: '14px', marginTop: '5px', opacity: 0.9 }}>
              {analysisData[0]?.isCurrentEnergyYear 
                ? '📊 Berekening gebaseerd op voorspelling (huidig energiejaar)' 
                : '✅ Berekening gebaseerd op werkelijke eenheden (vorig energiejaar)'}
            </span>
          )}
        </p>
        
        <div className="analysis-controls">
          <button 
            className="btn-settings"
            onClick={() => setShowPriceSettings(!showPriceSettings)}
          >
            ⚙️ Prijsinstellingen ({selectedEnergyYear})
          </button>
          
          <button 
            className="btn-refresh"
            onClick={() => fetchUsageData()}
            disabled={loading}
          >
            🔄 Ververs Data
          </button>

{/* Add last import date display */}
  {lastImportDate && (
    <div style={{
      fontSize: '12px',
      color: '#666',
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e9ecef'
    }}>
      📅 Laatste ISTA import:<br/>
      <strong>{new Date(lastImportDate).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      })}</strong>
    </div>
  )}


        </div>
      </div>

      {/* Price Settings Panel */}
      {showPriceSettings && (
        <div className="price-settings-panel">
          <h4>Prijsinstellingen voor {selectedEnergyYear}</h4>
          
          {lastSavedPrices && (
            <div className="price-last-saved">
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                <strong>Laatst opgeslagen:</strong> {new Date(lastSavedPrices.lastUpdated).toLocaleString('nl-NL')} 
                {lastSavedPrices.updatedBy && ` door ${lastSavedPrices.updatedBy}`}
              </p>
            </div>
          )}
          
          <div className="price-inputs">
            <div className="price-input-group">
              <label>Prijs per eenheid (variabel):</label>
              <input
                type="number"
                step="0.000001"
                value={energyPrices.variablePricePerUnit}
                onChange={(e) => setEnergyPrices(prev => ({
                  ...prev,
                  variablePricePerUnit: parseFloat(e.target.value) || 0
                }))}
                placeholder="0.600054"
                disabled={priceSettingsLoading}
              />
              <span className="currency">EUR</span>
            </div>
            
            <div className="price-input-group">
              <label>Prijs per vast punt:</label>
              <input
                type="number"
                step="0.000001"
                value={energyPrices.fixedPricePerUnit}
                onChange={(e) => setEnergyPrices(prev => ({
                  ...prev,
                  fixedPricePerUnit: parseFloat(e.target.value) || 0
                }))}
                placeholder="0.717296"
                disabled={priceSettingsLoading}
              />
              <span className="currency">EUR</span>
            </div>
          </div>
          
          <div className="price-explanation">
            <p><strong>Uitleg:</strong></p>
            <p>• <strong>Variabele kosten:</strong> {
              analysisData.length > 0 && analysisData[0]?.isCurrentEnergyYear 
                ? 'Voorspelde eenheden' 
                : 'Werkelijke eenheden'
            } × {energyPrices.variablePricePerUnit.toFixed(6)} EUR</p>
            <p>• <strong>Vaste kosten:</strong> {APARTMENT_SIZES.SMALL} (klein) of {APARTMENT_SIZES.LARGE} (groot) × {energyPrices.fixedPricePerUnit.toFixed(6)} EUR</p>
            <p>• <strong>Status:</strong> Te veel/weinig alleen bij >10% ÉN >€180 verschil</p>
            {analysisData.length > 0 && (
              <p style={{ marginTop: '10px', padding: '8px', backgroundColor: '#e8f5e8', borderRadius: '4px', fontSize: '13px' }}>
                <strong>📊 Berekeningsmetode:</strong> {
                  analysisData[0]?.isCurrentEnergyYear 
                    ? 'Gebruikt voorspelde eenheden omdat dit het huidige energiejaar is'
                    : 'Gebruikt werkelijke eenheden omdat dit een afgelopen energiejaar is'
                }
              </p>
            )}
          </div>
          
          <div className="price-actions">
            <button 
              className="btn-apply" 
              onClick={handlePriceUpdate}
              disabled={priceSettingsLoading}
            >
              {priceSettingsLoading ? '💾 Opslaan...' : '✅ Opslaan'}
            </button>
            <button 
              className="btn-cancel" 
              onClick={() => setShowPriceSettings(false)}
              disabled={priceSettingsLoading}
            >
              ❌ Annuleren
            </button>
            <button 
              className="btn-reset" 
              onClick={resetPriceSettings}
              disabled={priceSettingsLoading}
              style={{ 
                backgroundColor: '#6c757d', 
                color: 'white', 
                marginLeft: '10px',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                cursor: priceSettingsLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {priceSettingsLoading ? '🔄 Resetten...' : '🔄 Reset naar Standaard'}
            </button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisData.length > 0 ? (
        <div className="analysis-results">
          <div className="analysis-summary">
            <div className="summary-stats">
              <div className="stat-card">
                <span className="stat-number">{analysisData.length}</span>
                <span className="stat-label">Toren Appartementen</span>
              </div>
              <div className="stat-card red">
                <span className="stat-number">
                  {analysisData.filter(a => a.status === 'Te weinig betaald').length}
                </span>
                <span className="stat-label">Te weinig betaald</span>
              </div>
              <div className="stat-card orange">
                <span className="stat-number">
                  {analysisData.filter(a => a.status === 'Te veel betaald').length}
                </span>
                <span className="stat-label">Te veel betaald</span>
              </div>
              <div className="stat-card green">
                <span className="stat-number">
                  {analysisData.filter(a => a.status === 'OK').length}
                </span>
                <span className="stat-label">Op koers</span>
              </div>
            </div>
          </div>

          {/* Explanation above table */}
          <div className="calculation-explanation" style={{
            backgroundColor: '#e8f4f0',
            border: '1px solid #246834',
            borderRadius: '8px',
            padding: '15px',
            margin: '20px 0',
            fontSize: '14px',
            lineHeight: '1.5'
          }}>
            <p style={{ margin: '0', color: '#1e4d2b', fontWeight: '500' }}>
              <strong>📋 Berekeningsuitleg:</strong><br/>
              In het <strong>huidige energiejaar</strong> wordt de status en voorspelde kosten gebaseerd op de <em>voorspelde eenheden</em>.<br/>
              In <strong>voorgaande energiejaren</strong> is dit gebaseerd op de <em>werkelijke eenheden</em>.
            </p>
          </div>

          {/* Totals Summary Above Table */}
          <div className="totals-summary">
            <h4>📊 Totaaloverzicht {selectedEnergyYear}</h4>
            <div className="totals-grid">
              <div className="total-item">
                <span className="total-label">Vorig jaar totaal:</span>
                <span className="total-value">{formatNumber(totals.totalPreviousYear)} eenheden</span>
              </div>
              <div className="total-item">
                <span className="total-label">Werkelijke eenheden:</span>
                <span className="total-value">{formatNumber(totals.totalActualUnits)} eenheden</span>
              </div>
              <div className="total-item">
                <span className="total-label">Voorspelde eenheden:</span>
                <span className="total-value">{formatNumber(totals.totalPredictedUnits)} eenheden</span>
              </div>
              <div className="total-item">
                <span className="total-label">Voorspelde kosten:</span>
                <span className="total-value cost">{formatCurrency(totals.totalPredictedCosts)}</span>
              </div>
              <div className="total-item">
                <span className="total-label">Betaalde voorschotten:</span>
                <span className="total-value payment">{formatCurrency(totals.totalVoorschotten)}</span>
              </div>
              <div className="total-item">
                <span className="total-label">Totaal verschil:</span>
                <span className={`total-value difference ${totals.totalDifference >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(totals.totalDifference)}
                </span>
              </div>
            </div>
          </div>

          <div className="analysis-table-container">
            <table className="analysis-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('apartmentNumber')} style={{ cursor: 'pointer' }}>
                    🏠 App.{getSortIndicator('apartmentNumber')}
                  </th>
                  <th onClick={() => handleSort('residentName')} style={{ cursor: 'pointer' }}>
                    👤 Bewoner{getSortIndicator('residentName')}
                  </th>
                  <th onClick={() => handleSort('apartmentSize')} style={{ cursor: 'pointer' }}>
                    📏 Grootte{getSortIndicator('apartmentSize')}
                  </th>
                  <th onClick={() => handleSort('previousYearTotal')} style={{ cursor: 'pointer' }}>
                    📊 Vorig jaar{getSortIndicator('previousYearTotal')}
                  </th>
                  <th onClick={() => handleSort('actualUnits')} style={{ cursor: 'pointer' }}>
                    ⚡ Eenheden{getSortIndicator('actualUnits')}
                  </th>
                  <th onClick={() => handleSort('predictedUnits')} style={{ cursor: 'pointer' }}>
                    🔮 Voorspelling{getSortIndicator('predictedUnits')}
                  </th>
                  <th onClick={() => handleSort('totalPredictedCosts')} style={{ cursor: 'pointer' }}>
                    💰 Voorspelde Kosten{getSortIndicator('totalPredictedCosts')}
                  </th>
                  <th onClick={() => handleSort('totalVoorschotten')} style={{ cursor: 'pointer' }}>
                    💳 Voorschotten{getSortIndicator('totalVoorschotten')}
                  </th>
                  <th onClick={() => handleSort('difference')} style={{ cursor: 'pointer' }}>
                    📊 Verschil{getSortIndicator('difference')}
                  </th>
                  <th onClick={() => handleSort('percentageDifference')} style={{ cursor: 'pointer' }}>
                    📈 %{getSortIndicator('percentageDifference')}
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                    🎯 Status{getSortIndicator('status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAnalysisData.map((apartment, index) => (
                  <tr key={apartment.apartmentNumber} className={`status-${apartment.statusColor}`}>
                    <td className="apartment-number">{apartment.apartmentNumber}</td>
                    <td className="resident-name" title={apartment.residentName}>
                      {apartment.residentName}
                    </td>
                    <td className="apartment-size">
                      <span className={`size-badge ${apartment.apartmentSize.toLowerCase()}`}>
                        {apartment.apartmentSize === 'SMALL' ? 'Klein' : 'Groot'}
                      </span>
                    </td>
                    <td className="previous-year-usage">{formatNumber(apartment.previousYearTotal)}</td>
                    <td className="usage-units">{formatNumber(apartment.actualUnits)}</td>
                    <td className="predicted-units">{formatNumber(apartment.predictedUnits)}</td>
                    <td className="predicted-costs">
                      <div className="cost-breakdown">
                        <span className="total">{formatCurrency(apartment.totalPredictedCosts)}</span>
                        <small>
                          Var: {formatCurrency(apartment.variableCosts)} + 
                          Vast: {formatCurrency(apartment.fixedCosts)}
                        </small>
                      </div>
                    </td>
                    <td className="voorschotten">
                      <div className="voorschot-info">
                        <span className="total">{formatCurrency(apartment.totalVoorschotten)}</span>
                        <small>Ø {formatCurrency(apartment.monthlyAverage)}/mnd</small>
                      </div>
                    </td>
                    <td className={`difference ${apartment.difference >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(apartment.difference)}
                    </td>
                    <td className={`percentage ${apartment.percentageDifference >= 0 ? 'positive' : 'negative'}`}>
                      {apartment.percentageDifference >= 0 ? '+' : ''}{apartment.percentageDifference.toFixed(1)}%
                    </td>
                    <td className="status">
                      <span className={`status-badge ${apartment.statusColor}`}>
                        {apartment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Additional Totals Summary Below Table */}
          <div className="totals-footer">
            <div className="footer-totals">
              <div className="footer-total-section">
                <h5>💰 Kostenverdeling</h5>
                <p>Variabele kosten: <strong>{formatCurrency(totals.totalVariableCosts)}</strong></p>
                <p>Vaste kosten: <strong>{formatCurrency(totals.totalFixedCosts)}</strong></p>
                <p>Totale kosten: <strong>{formatCurrency(totals.totalPredictedCosts)}</strong></p>
              </div>
              <div className="footer-total-section">
                <h5>💳 Voorschotten</h5>
                <p>Totaal betaald: <strong>{formatCurrency(totals.totalVoorschotten)}</strong></p>
                <p>Gemiddeld per appartement: <strong>{formatCurrency(totals.totalVoorschotten / Math.max(totals.totalApartments, 1))}</strong></p>
              </div>
              <div className="footer-total-section">
                <h5>📊 Resultaat</h5>
                <p className={totals.totalDifference >= 0 ? 'positive' : 'negative'}>
                  {totals.totalDifference >= 0 ? 'Totaal overschot' : 'Totaal tekort'}: 
                  <strong> {formatCurrency(Math.abs(totals.totalDifference))}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-data">
          <p>🔍 Geen analysegegevens beschikbaar.</p>
          <p>Zorg ervoor dat er verbruiksgegevens en voorschotten beschikbaar zijn voor het geselecteerde energiejaar.</p>
        </div>
      )}
    </div>
  );
};

export default VoorschotAnalysis;